import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Settings, User, MapPin, BookMarked, LogOut, Coins } from "lucide-react";
import { logout } from "@/features/user/userSlice";
import { fetchBalanceSuccess } from "@/features/tokens/tokensSlice";
import {useAppDispatch, useAppSelector} from "@/lib/hooks.ts";
import { useNavigate } from "react-router-dom";
import { USER_TOKEN, USER } from "@/api/apiConfig";
import {Guide} from "@/features/guides/guidesSlice.ts";

interface ProfileSidebarProps {
  user: {
    id?: string;
    name?: string;
    email?: string;
    avatar?: string;
    last_guides?: Guide[];
    saved_guides?: Guide[];
  };
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const ProfileSidebar = ({ user, activeTab, onTabChange }: ProfileSidebarProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { balance } = useAppSelector(state => state.tokens);

  const name = (fullname) => {
    return fullname.split(' ')?.map(n => n[0]).join('');
  }

  const handleLogout = () => {
    dispatch(logout());
    dispatch(fetchBalanceSuccess(0));

    localStorage.removeItem(USER_TOKEN);
    localStorage.removeItem(USER);

    navigate('/');
  };

  return (
    <Card className="w-full md:w-64 p-4 md:p-6">
      <div className="flex flex-col items-center mb-4 md:mb-6">
        <Avatar className="w-20 h-20 md:w-24 md:h-24 mb-3 md:mb-4">
          <AvatarImage src={user.avatar} alt={user.name} />
          <AvatarFallback>{ name(user.name) }</AvatarFallback>
        </Avatar>
        <h2 className="text-lg md:text-xl font-semibold text-center">{user.name}</h2>
        <div className="mt-3 md:mt-4 flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full">
          <Coins className="h-4 w-4 md:h-5 md:w-5 text-primary" />
          <span className="text-sm md:text-base font-medium">{balance} tokens</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
        <Button
          variant={activeTab === "profile" ? "default" : "ghost"}
          className="w-full justify-start h-10 md:h-11 text-sm md:text-base"
          onClick={() => onTabChange("profile")}
        >
          <User className="mr-1.5 md:mr-2 h-4 w-4" />
          Profile
        </Button>
        <Button
          variant={activeTab === "guides" ? "default" : "ghost"}
          className="w-full justify-start h-10 md:h-11 text-sm md:text-base"
          onClick={() => onTabChange("guides")}
        >
          <MapPin className="mr-1.5 md:mr-2 h-4 w-4" />
          My Guides
        </Button>
        <Button
          variant={activeTab === "saved" ? "default" : "ghost"}
          className="w-full justify-start h-10 md:h-11 text-sm md:text-base"
          onClick={() => onTabChange("saved")}
        >
          <BookMarked className="mr-1.5 md:mr-2 h-4 w-4" />
          Saved Guides
        </Button>
        <Button
          variant={activeTab === "settings" ? "default" : "ghost"}
          className="w-full justify-start h-10 md:h-11 text-sm md:text-base"
          onClick={() => onTabChange("settings")}
        >
          <Settings className="mr-1.5 md:mr-2 h-4 w-4" />
          Settings
        </Button>
        <Button
          variant="ghost"
          className="w-full col-span-2 md:col-span-1 justify-start h-10 md:h-11 text-sm md:text-base text-destructive hover:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="mr-1.5 md:mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </Card>
  );
};
